import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { canSeeInventory } from '@infras/shared-types';
import { Pager } from '../components/Pager';
import { RefreshButton } from '../components/RefreshButton';
import { Icon, type IconName } from '../components/Icon';
import { FilterTokenBar } from '../components/requests/FilterTokenBar';
import { SavedViews, usePersonalViews } from '../components/requests/SavedViews';
import { RequestRowFrame } from '../components/requests/RequestRow';
import { LabelChip, type LabelLite } from '../components/labels/LabelChip';
import { toLabelLite } from '../components/labels/LabelPicker';
import { statusBadgeClass } from '../lib/access';
import { formatDateTime } from '../lib/format';
import { useMe } from '../lib/useMe';
import { useUrlNumber, useUrlState } from '../lib/useUrlState';
import { relTime, shortDate } from '../lib/requestChips';
import {
  REQUEST_VIEWS,
  dueTone,
  invalidRequestTokens,
  normalizeRequestQuery,
  requestLanguage,
  toRequestFilter,
} from '../lib/requestFilter';
import '../styles/requests-shared.css';
import { REQUEST_VIEWS_KEY, migrateLegacyRequestViews } from '../lib/migrateSavedViews';
import '../styles/requests-list.css';
import '../styles/list.css';

/**
 * The one tasks list. Every task — with an access grant or without —
 * is the same kind of row; the filter is ONE url param (`q`) in the language of
 * `lib/requestFilter.ts`, sent to the server's `tasks(filter)`. Paging is
 * server-side. New members see the tasks the API lets them see (every access task plus the
 * plain tasks they filed), and no engineer-only views.
 */
const TASKS = gql`
  query TasksList($filter: TaskFilterInput) {
    tasks(filter: $filter) {
      total
      items {
        id
        title
        template
        requesterEmail
        assignees {
          email
        }
        dueAt
        state
        closeReason
        createdAt
        updatedAt
        legacyCrId
        commentCount
        labels {
          id
          name
          scope
          color
          system
        }
        access {
          status
          resourceType
          resourceName
        }
      }
    }
  }
`;

const LABEL_NAMES = gql`
  query TaskListLabels {
    labels {
      id
      name
    }
  }
`;

interface Row {
  id: number;
  title: string;
  template: string;
  requesterEmail: string;
  assignees: { email: string }[];
  dueAt?: string | null;
  state: string;
  closeReason?: string | null;
  createdAt: string;
  updatedAt: string;
  legacyCrId?: number | null;
  commentCount: number;
  labels: {
    id: number;
    name: string;
    scope: string | null;
    color: string;
    system: boolean;
  }[];
  access?: {
    status: string;
    resourceType: string;
    resourceName: string;
  } | null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const who = (email: string, me: string) => (email.toLowerCase() === me ? 'you' : email.split('@')[0]);
const reasonText = (s?: string | null) => (s ?? 'closed').replace('_', ' ');

function statusOf(r: Row): { icon: IconName; mod: string; label: string } {
  if (r.state === 'closed') {
    if (r.closeReason === 'done')
      return {
        icon: 'check',
        mod: 'req-row__icon--ok',
        label: 'Closed · done',
      };
    return {
      icon: 'cross',
      mod: 'req-row__icon--off',
      label: `Closed · ${reasonText(r.closeReason)}`,
    };
  }
  if (r.access?.status === 'pending') return { icon: 'clock', mod: '', label: 'Open · access pending' };
  return { icon: 'inbox', mod: '', label: 'Open' };
}

/** Workflow label first, then other scoped labels, then plain ones. */
function sortLabels(ls: LabelLite[]): LabelLite[] {
  const rank = (l: LabelLite) => (l.scope === 'workflow' ? 0 : l.scope ? 1 : 2);
  return [...ls].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

function RowView({ r, me, now }: { r: Row; me: string; now: number }) {
  const tone = r.state === 'open' && r.dueAt ? dueTone(r.dueAt, now) : null;
  return (
    <RequestRowFrame
      testid="req-list-row"
      id={String(r.id)}
      dataKind={r.access ? 'access' : r.template}
      mine={r.requesterEmail.toLowerCase() === me}
      status={statusOf(r)}
      href={`/tasks/${r.id}`}
      titleAttr={r.title}
      title={r.title}
      meta={
        <>
          #{r.id}
          {r.legacyCrId != null && <span className="muted"> (was CR-{r.legacyCrId})</span>} · opened{' '}
          <span title={formatDateTime(r.createdAt)}>{relTime(r.createdAt, now)}</span> by{' '}
          <strong title={r.requesterEmail}>{who(r.requesterEmail, me)}</strong>
          {r.assignees.length > 0 && (
            <>
              {' '}
              · assigned to{' '}
              <strong title={r.assignees.map((a) => a.email).join(', ')}>
                {who(r.assignees[0].email, me)}
                {r.assignees.length > 1 ? ` +${r.assignees.length - 1}` : ''}
              </strong>
            </>
          )}
        </>
      }
      chips={
        <>
          {/* ONE state chip: an access ticket shows its grant outcome (which already says how
              it closed); any other closed ticket shows its close reason. */}
          {r.access ? (
            <span
              className={statusBadgeClass(r.access.status)}
              data-testid={r.state === 'closed' ? 'req-list-closed' : undefined}
              title={`${r.access.resourceType}: ${r.access.resourceName}`}
            >
              Access · {r.access.status}
            </span>
          ) : r.state === 'closed' ? (
            // Neutral (muted) like every other closed/meta pill: amber means "needs attention".
            <span className="badge" data-testid="req-list-closed">
              Closed · {reasonText(r.closeReason)}
            </span>
          ) : null}
          {r.dueAt && (
            <span
              className={`req-chip${tone === 'over' ? ' req-chip--due' : tone === 'soon' ? ' req-chip--warn' : ''}`}
              title={formatDateTime(r.dueAt)}
            >
              <Icon name="clock" size={13} className="req-ico" />
              {tone === 'over' ? 'overdue · ' : 'due '}
              {shortDate(r.dueAt)}
            </span>
          )}
          {sortLabels(r.labels.map(toLabelLite)).map((l) => (
            <LabelChip key={l.id} label={l} size="sm" />
          ))}
        </>
      }
      commentCount={r.commentCount}
      people={r.assignees.map((a) => a.email)}
      updatedAt={r.updatedAt}
      now={now}
    />
  );
}

export function TasksPage() {
  const { me } = useMe();
  const engineer = !!me && canSeeInventory(me.role);
  return (
    <div className="page page--wide" data-testid="req-list">
      <div className="detail-head req-list-head">
        <h2>Tasks</h2>
        <div style={{ flex: 1 }} />
        <div className="rql-head-actions">
          {engineer && (
            <Link to="/tasks/board" className="btn" data-testid="req-list-board">
              <Icon name="grid" size={14} /> Board
            </Link>
          )}
          <Link to="/tasks/new" className="btn btn-primary" data-testid="req-list-new">
            + New task
          </Link>
        </div>
      </div>
      <p className="detail-desc">
        Access, infra changes and anything else you need from DevOps, in one list. Filter with tokens such as{' '}
        <code>label:</code>, <code>state:</code> or <code>assignee:me</code>; saved views keep your usual cuts.
      </p>
      {me && !engineer && (
        <div className="cr-note" data-testid="req-list-member-note">
          <Icon name="shield" className="cr-note__icon" />
          <div>
            <strong>You see tasks with an access grant, plus the tasks you filed.</strong> Other tasks are
            visible to the <strong>engineer</strong> role and above. Ask an admin in #devops-infra if you need them.
          </div>
        </div>
      )}
      {me && <List me={me.email.toLowerCase()} engineer={engineer} />}
    </div>
  );
}

function List({ me, engineer }: { me: string; engineer: boolean }) {
  const [q, setQ] = useUrlState('q');
  const [sort, setSort] = useUrlState('sort', 'updated');
  const [page, setPage] = useUrlNumber('page', 1);
  const [perPage, setPerPage] = useState(20);
  const [adding, setAdding] = useState(false);
  // Runs before usePersonalViews reads storage (hook order): carry the old lists' personal
  // views over once.
  useState(() => migrateLegacyRequestViews());
  const personal = usePersonalViews(REQUEST_VIEWS_KEY, normalizeRequestQuery);

  const labelsQ = useQuery<{ labels: { id: string; name: string }[] }>(LABEL_NAMES, {
    fetchPolicy: 'cache-first',
  });
  const lang = useMemo(
    () => requestLanguage((labelsQ.data?.labels ?? []).map((l) => l.name).slice(0, 40)),
    [labelsQ.data],
  );

  const dq = useDebounced(q, 250);
  const parsed = useMemo(() => lang.parse(dq), [lang, dq]);
  // A token the server cannot use is never sent: dropping it would quietly widen the list.
  const invalid = useMemo(() => invalidRequestTokens(lang.parse(q)), [lang, q]);
  // Stable per minute, so a re-render does not change `dueBefore` (and refetch) every render.
  const minute = Math.floor(Date.now() / 60_000);
  const variables = useMemo(() => {
    const f = toRequestFilter(parsed, me, minute * 60_000);
    return {
      filter: {
        ...f,
        sort: sort === 'due' ? 'due' : 'updated',
        limit: perPage,
        offset: (page - 1) * perPage,
      },
    };
  }, [parsed, me, minute, sort, perPage, page]);

  const { data, previousData, loading, error, refetch } = useQuery<{
    tasks: { total: number; items: Row[] };
  }>(TASKS, {
    variables,
    fetchPolicy: 'cache-and-network',
    // both: the debounced query must not fire while it, or what is typed, holds an invalid token
    skip: invalid.length > 0 || invalidRequestTokens(parsed).length > 0,
  });
  const shown = data ?? previousData;
  const now = useMemo(() => Date.now(), [shown]);

  const rows = shown?.tasks.items ?? [];
  const total = shown?.tasks.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / perPage));
  const cur = Math.min(page, pageCount);
  // `?page=99` past the end: once this query's total is known, replace the URL page with the
  // last page so the offset is recomputed from the clamped page (an empty page is never shown).
  useEffect(() => {
    if (data && page > pageCount) setPage(pageCount);
  }, [data, page, pageCount, setPage]);
  const start = (cur - 1) * perPage;

  // Engineer-only views ("Waiting on me", "Assigned to me", …): a new member can never be
  // assigned or decide, so those views would always be empty for them.
  const presets = REQUEST_VIEWS.filter((v) => engineer || !v.reviewerOnly);
  const normQ = normalizeRequestQuery(q);
  const activeView = [...presets, ...personal.views.map((v) => ({ slug: 'custom', q: v.q }))].find(
    (v) => normalizeRequestQuery(v.q) === normQ,
  );
  // One query per page: the count shows on the active view only.
  const count = (viewQ: string) => (normalizeRequestQuery(viewQ) === normQ && data && dq === q ? total : null);

  return (
    <>
      <SavedViews
        presets={presets}
        personal={personal.views}
        q={q}
        count={count}
        normalize={normalizeRequestQuery}
        onSelect={(v) => {
          setAdding(false);
          setQ(v);
        }}
        onSave={(name) => personal.save(name, q)}
        onRemove={personal.remove}
        adding={adding}
        setAdding={setAdding}
      />
      <FilterTokenBar q={q} onChange={setQ} lang={lang} />
      <div className="req-countline">
        {invalid.length ? (
          <span data-testid="req-list-count" className="req-countline__invalid" role="status">
            Filter not applied: {invalid.map((t) => `${lang.keyLabel(t.key)} “${t.value}”`).join(', ')}{' '}
            {invalid.length === 1 ? 'is not a known value' : 'are not known values'}.
          </span>
        ) : (
          <span className="req-countline__left">
            <span data-testid="req-list-count">
              <strong>{total}</strong> task{total === 1 ? '' : 's'}
              {normQ ? (total === 1 ? ' matches' : ' match') : ''}
            </span>
            {normQ && !activeView && !adding && (
              <>
                <span aria-hidden="true">·</span>
                <button type="button" className="req-linkbtn" onClick={() => setAdding(true)}>
                  Save as view
                </button>
              </>
            )}
          </span>
        )}
        <span className="req-countline__right">
          <select
            className="class-select"
            value={sort === 'due' ? 'due' : 'updated'}
            title="Sort"
            aria-label="Sort"
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="updated">Recently updated</option>
            <option value="due">Due date</option>
          </select>
          <select
            className="class-select"
            value={perPage}
            title="Rows per page"
            aria-label="Rows per page"
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
          <RefreshButton refetch={() => refetch().catch(() => undefined)} />
        </span>
      </div>
      {invalid.length ? (
        <div className="empty-state" data-testid="req-list-invalid">
          <div className="empty-title">Fix or remove the highlighted filter to see results.</div>
          <div className="muted">
            Valid values are in <strong>Add filter</strong>.
          </div>
        </div>
      ) : error ? (
        <p className="error">{error.message}</p>
      ) : loading && !shown ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          <div className="empty-icon">
            <Icon name="inbox" size={30} />
          </div>
          <div className="empty-title">
            {activeView?.slug === 'assigned'
              ? 'Nothing is assigned to you'
              : activeView?.slug === 'waiting-on-me'
                ? 'Nothing is waiting on you'
                : normQ
                  ? 'Nothing matches these filters.'
                  : 'Nothing here yet.'}
          </div>
          <div className="empty-actions btn-row">
            {normQ && normQ !== 'state:open' && (
              <button type="button" className="btn btn-sm" onClick={() => setQ('state:open')}>
                View all open
              </button>
            )}
            {normQ && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setQ('')}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <ul className={`req-rows${loading ? ' rql-stale' : ''}`}>
            {rows.map((r) => (
              <RowView key={r.id} r={r} me={me} now={now} />
            ))}
          </ul>
          <div className="req-pager">
            <Pager page={cur} pageCount={pageCount} total={total} start={start} pageSize={perPage} onPage={setPage} />
          </div>
        </>
      )}
    </>
  );
}
